import React, { forwardRef, useState } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  InputGroup,
  InputLeftElement,
  Input as ChakraInput,
  Textarea,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { FaExpandAlt, FaCompressAlt } from 'react-icons/fa';
import Icon from './Icon';

const InputBase = (
  {
    name,
    label,
    error = null,
    iconLeft,
    iconRight,
    placeholder = 'Digite...',
    labelSize = 'xs',
    children,
    expandable = false,
    isDisabled = false,
    isViewMode = false,
    ...rest
  },
  ref
) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Shared styles for both disabled and viewMode states
  const viewModeStyles = {
    background: '#F5F5FB',
    color: '#292B30',
    opacity: 1,
    border: '1px solid #F5F5FB',
    cursor: 'default',
    _disabled: {
      background: '#F5F5FB !important',
      color: '#292B30 !important',
      opacity: 1,
      border: '1px solid #F5F5FB',
      cursor: 'default',
      WebkitBoxShadow: '0 0 0px 1000px #F5F5FB inset',
      WebkitTextFillColor: '#292B30',
    },
    _hover: {
      background: '#F5F5FB',
      border: '1px solid #F5F5FB',
    },
  };

  const commonProps = {
    name,
    id: name,
    placeholder,
    fontSize: 'sm',
    fontWeight: 600,
    isDisabled: isDisabled,
    // Apply viewMode styles when in viewMode, otherwise use default styles
    ...(isViewMode ? viewModeStyles : {
      border: '1px solid #F5F5FB',
      bg: '#F5F5FB',
      borderRadius: '8px',
      _autofill: {
        bg: '#F5F5FB !important',
        WebkitBoxShadow: '0 0 0px 1000px #F5F5FB inset',
        WebkitTextFillColor: '#292B30',
      },
      _placeholderShown: {
        bg: 'white',
        borderColor: '#CCCFD1',
      },
    }),
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <FormControl isInvalid={!!error}>
      {label && <FormLabel htmlFor={name}>{label}</FormLabel>}

      <InputGroup>
        {iconLeft && (
          <InputLeftElement height='full' pl='16px'>
            <Icon icon={iconLeft} />
          </InputLeftElement>
        )}

        <Flex
          position='relative'
          width='100%'
          transition='all 0.3s ease-in-out'
          height={isExpanded ? 'auto' : '48px'}
        >
          {expandable && isExpanded ? (
            <Textarea
              {...commonProps}
              {...rest}
              ref={ref}
              resize='vertical'
              minHeight='120px'
              p='4'
              w='100%'
              overflowY='auto'
              whiteSpace='pre-wrap'
              wordBreak='break-word'
              transition='all 0.3s ease-in-out'
              opacity={isExpanded ? 1 : 0}
              transform={isExpanded ? 'translateY(0)' : 'translateY(-10px)'}
              readOnly={isViewMode}
              // Apply the same viewMode styles to textarea
              {...(isViewMode && viewModeStyles)}
            />
          ) : (
            <ChakraInput
              {...commonProps}
              {...rest}
              ref={ref}
              height='48px'
              minW='140px'
              pl={iconLeft ? '50px' : '16px'}
              pr={expandable ? '40px' : '16px'}
              readOnly={isViewMode}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
          )}
          
          {/* Always show expand button if expandable is true, regardless of viewMode */}
          {expandable && (
            <IconButton
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              icon={isExpanded ? <FaCompressAlt /> : <FaExpandAlt />}
              onClick={handleToggleExpand}
              position='relative'
              right={isExpanded ? '-2' : '-2'}
              top={isExpanded ? '50%' : '50%'}
              transform='translateY(-50%)'
              p='2'
              color='gray.300'
              fontSize='sm'
              zIndex='1'
              bg='transparent'
              borderRadius='20px'
              _hover={{ bg: 'gray.100' }}
              title={isExpanded ? 'Compress input' : 'Expand input'}
            />
          )}
        </Flex>
      </InputGroup>

      {!!error && <FormErrorMessage>{error.message}</FormErrorMessage>}
    </FormControl>
  );
};

export const InputText = forwardRef(InputBase);